import { Path, useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FormRow from "components/form/FormRow";
import { MESSAGE } from "constants/validate";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import ButtonComponent from "components/button/ButtonComponent";
import { formatDateTime } from "utils";
import { memo, useCallback, useEffect, useMemo } from "react";
import { TFieldType } from "type";
import { MstVendorDetail, Vendor } from "../types/vendor";
import { getSendPatternText } from "../constants/sendPattern";

// Constants
const FIELD_STYLES = {
  width: "100%",
  height: "48px",
  boxSizing: "border-box",
  backgroundColor: "var(--white)",
  borderRadius: 0,
  border: "3px solid var(--silver-150)",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
} as const;

const STATUS_OPTIONS = [
  { label: "有効", value: 1 },
  { label: "無効", value: 0 },
];

const REBATE_CATE_OPTIONS = [
  { label: "非通通", value: 0 },
  { label: "通通", value: 1 },
];

const RECEIVING_SETTING_OPTIONS = [
  { label: "", value: "" },
  { label: "オン", value: 1 },
  { label: "オフ", value: 0 },
];

const toReceivingSettingValue = (raw: unknown): number | "" => {
  if (raw === 1 || raw === "1") return 1;
  if (raw === 0 || raw === "0") return 0;
  return "";
};

// Types
interface IVendorFormField {
  label: string;
  fieldName: keyof MstVendorDetail;
  disabled?: boolean;
  type?: TFieldType;
  rules?: any;
  options?: { label: string; value: number | string }[];
}

interface VendorFormProps {
  visible: boolean;
  onClose: () => void;
  onHandleSubmit: (form: MstVendorDetail) => void;
  isLoading: boolean;
  vendorDetail: Vendor;
}

// Hook for form fields configuration
const useVendorFormFields = (): IVendorFormField[] =>
  useMemo(
    () => [
      { label: "仕入先コード", disabled: true, fieldName: "yh_vendor_cd" },
      { label: "仕入先名", disabled: true, fieldName: "yh_vendor_name" },
      { label: "企業コード", disabled: true, fieldName: "company_cd" },
      { label: "送信コード", disabled: true, fieldName: "send_pattern" },
      { label: "リベート率", disabled: true, fieldName: "rebate_rate" },
      {
        label: "基本リベート対象区分",
        disabled: true,
        type: "select",
        fieldName: "base_rebate_category",
        options: REBATE_CATE_OPTIONS,
      },
      {
        label: "仕入先名称",
        fieldName: "mst_vendor_vendor_name",
        rules: {
          required: MESSAGE.EMPTY,
          maxLength: { value: 1000, message: MESSAGE.MAX_LENGTH(1000) },
        },
      },
      {
        label: "メールアドレス（連絡先）",
        fieldName: "mst_vendor_email_address",
        type: "email",
        rules: {
          required: MESSAGE.EMPTY,
          maxLength: { value: 320, message: MESSAGE.MAX_LENGTH(320) },
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "有効なメールアドレスを入力してください",
          },
        },
      },
      {
        label: "認証コード",
        disabled: true,
        fieldName: "mst_vendor_auth_cd",
      },
      {
        label: "認証コードを作成または更新",
        fieldName: "auth_cd_update_flg",
        type: "checkbox",
      },
      {
        label: "出荷受取方法",
        fieldName: "mst_vendor_shipping_receipt_method",
        rules: {
          required: MESSAGE.EMPTY,
          maxLength: { value: 320, message: MESSAGE.MAX_LENGTH(320) },
        },
      },
      {
        label: "受信設定",
        type: "select",
        fieldName: "mst_vendor_receiving_setting",
        options: RECEIVING_SETTING_OPTIONS,
      },
      {
        label: "欠品判定期間閾値",
        fieldName: "mst_vendor_shortage_period",
        type: "number",
        rules: {
          required: MESSAGE.EMPTY,
          maxLength: { value: 9, message: MESSAGE.MAX_LENGTH(9) },
        },
      },
      {
        label: "有効/無効",
        type: "select",
        fieldName: "mst_vendor_status_flag",
        rules: {
          required: MESSAGE.EMPTY,
        },
        options: STATUS_OPTIONS,
      },
    ],
    [],
  );

// Hook for initial form values
const useVendorFormValues = (vendorDetail: Vendor): MstVendorDetail =>
  useMemo(
    () => ({
      yh_vendor_cd: vendorDetail.vendor_cd || "",
      yh_vendor_name: vendorDetail.yh_vendor_name || "",
      company_cd: vendorDetail.company_cd || "",
      send_pattern: getSendPatternText(vendorDetail.send_pattern) as never,
      rebate_rate: vendorDetail.rebate_rate || "",
      mst_vendor_vendor_name: vendorDetail.mst_vendor_vendor_name || "",
      mst_vendor_email_address: vendorDetail.mst_vendor_email_address || "",
      mst_vendor_shipping_receipt_method: vendorDetail.mst_vendor_shipping_receipt_method || "",
      mst_vendor_receiving_setting: toReceivingSettingValue(vendorDetail.mst_vendor_receiving_setting),
      mst_vendor_status_flag: vendorDetail.mst_vendor_status_flag ?? 1,
      mst_vendor_updated_at: vendorDetail.mst_vendor_updated_at || "",
      updated_at: vendorDetail.updated_at || "",
      mst_vendor_auth_cd: vendorDetail.mst_vendor_auth_cd || "",
      auth_cd_update_flg: 0,
      base_rebate_category: vendorDetail.base_rebate_category || 0,
      mst_vendor_shortage_period: vendorDetail.mst_vendor_shortage_period,
    }),
    [vendorDetail],
  );

// Header Component
const ModalHeader = memo(
  ({ vendorDetail, onClose }: { vendorDetail: Vendor; onClose: () => void }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
      <Typography
        variant="h4"
        component="h2"
        sx={{
          fontWeight: "fontWeightMedium",
          color: "var(--gray-700)",
          fontSize: { xs: 28, md: 33 },
        }}
      >
        仕入先詳細
        <Typography component="br" sx={{ display: { xs: "block", md: "none" } }} />
        <Typography
          component="span"
          sx={{
            fontSize: { xs: 16, md: 21 },
            fontWeight: "fontWeightMedium",
            color: "var(--gray-700)",
            ml: { xs: 0, md: 3 },
          }}
        >
          最終連携日 {formatDateTime(vendorDetail.updated_at)}
        </Typography>
      </Typography>
      <CancelButton onClose={onClose} />
    </Box>
  ),
);

ModalHeader.displayName = "ModalHeader";

// Form Field Component
const VendorFormField = memo(
  ({ field, control, disabled }: { field: IVendorFormField; control: any; disabled?: boolean }) => (
    <FormRow
      sxLabel={{ pl: 4, width: "40%" }}
      label={field.label}
      sxBoxField={{ width: "60%", flexGrow: 1 }}
      sxField={FIELD_STYLES}
      name={field.fieldName as Path<MstVendorDetail>}
      type={field.type as never}
      control={control}
      rules={field.rules}
      options={field.options || []}
      disabled={disabled || field.disabled}
    />
  ),
);

VendorFormField.displayName = "VendorFormField";

// Footer Component
const ModalFooter = memo(
  ({
    vendorDetail,
    onClose,
    isLoading,
    hasErrors,
  }: {
    vendorDetail: Vendor;
    onClose: () => void;
    isLoading: boolean;
    hasErrors: boolean;
  }) => (
    <>
      <ButtonComponent
        disabled={isLoading || hasErrors}
        type="submit"
        label="更新"
        theme="dark"
        sx={{ height: "50px", minWidth: "185px", width: "28%", mt: "25px" }}
      />

      <Box display="flex" alignItems="center" columnGap={3} mt={3.5}>
        <CancelButton onClose={onClose} />
        <Typography
          sx={{ fontSize: { xs: 16, md: 21 } }}
          fontWeight="medium"
          color="var(--gray-600)"
        >
          更新日 {formatDateTime(vendorDetail.mst_vendor_updated_at)}
        </Typography>
      </Box>
    </>
  ),
);

ModalFooter.displayName = "ModalFooter";

/**
 * VendorFormModal Component
 *
 * Modal form for viewing and editing vendor details.
 * Uses react-hook-form for form state management and validation.
 * Displays vendor fields, handles form submission, and resets form values when vendor details change.
 * @param {object} props - Component props.
 * @param {boolean} props.visible - Whether the modal is visible.
 * @param {() => void} props.onClose - Callback to close the modal.
 * @param {(form: MstVendorDetail) => void} props.onHandleSubmit - Callback for form submission.
 * @param {boolean} props.isLoading - Loading state for the form.
 * @param {Vendor} props.vendorDetail - Vendor detail data for the form.
 */
export default function VendorFormModal({
  visible,
  onClose,
  onHandleSubmit,
  isLoading,
  vendorDetail,
}: VendorFormProps) {
  // Custom hooks
  const formFields = useVendorFormFields();
  const initialValues = useVendorFormValues(vendorDetail);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MstVendorDetail>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialValues,
  });

  // Memoized values
  const hasErrors = Object.keys(errors).length > 0;

  // Callbacks
  const onSubmit = useCallback(
    (data: MstVendorDetail) => {
      if (isLoading || hasErrors) return;
      onHandleSubmit(data);
    },
    [isLoading, hasErrors, onHandleSubmit],
  );

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  // Effects
  useEffect(() => {
    if (vendorDetail?.id) {
      reset(initialValues);
    }
  }, [vendorDetail?.id, reset, initialValues]);

  if (!vendorDetail) {
    return null;
  }

  return (
    <Modal
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={visible}
      onClose={handleClose}
      disableEscapeKeyDown={isLoading}
    >
      <Box
        sx={{
          maxHeight: "90vh",
          overflow: "auto",
          width: "80%",
          maxWidth: "870px",
          backgroundColor: "var(--white)",
          padding: { xs: 3, md: 4, xl: "30px 70px" },
          borderRadius: "21px",
          display: "flex",
          flexDirection: "column",
          rowGap: "20px",
        }}
      >
        <ModalHeader vendorDetail={vendorDetail} onClose={handleClose} />

        <form onSubmit={handleSubmit(onSubmit)}>
          {formFields.map((field) => (
            <VendorFormField
              key={field.fieldName}
              field={field}
              control={control}
              disabled={isLoading}
            />
          ))}

          <ModalFooter
            vendorDetail={vendorDetail}
            onClose={handleClose}
            isLoading={isLoading}
            hasErrors={hasErrors}
          />
        </form>
      </Box>
    </Modal>
  );
}
